import { Form, json, useActionData, useLoaderData } from 'react-router-dom';
import Joi from 'joi';
import { fetchClient } from '../../util/fetchClient';
import logo from '../../logo.svg';

const recoverPasswordChangeSchema = Joi.object({
  email: Joi.string().email({ tlds: { allow: false } }).required(),
  recovery_code: Joi.string().uuid({ version: 'uuidv4'}).required(),
  password: Joi.string().required(),
  password_check: Joi.string().required()
}).options({ abortEarly: false });

export const loader = async ({ request }) => {
  const searchParams = new URL(request.url).searchParams;
  const email = searchParams.get('email');
  const recoveryCode = searchParams.get('recovery_code');

  return { email, recoveryCode };
};

export const action = async ({ request }) => {
  const data = Object.fromEntries(await request.formData());

  try {
    await recoverPasswordChangeSchema.validateAsync(data);
  } catch (validationError) {
    const errors = {};
    validationError?.details.map((details) => errors[details.context.key] = details.message);

    return json({ errors });
  }

  const { email, recovery_code: recoveryCode, password, password_check: passwordCheck } = data;

  if (password !== passwordCheck) {
    return json({ errors: {
      password: 'Mismatching password and password check'
    } });
  }

  const response = await fetchClient('/v1/auth/recover/password/change', { method: 'POST', body: JSON.stringify({ email, recovery_code: recoveryCode, password }) });

  if (response.response.ok) {
    return json({ success: true });
  } else {
    return json({ success: false, errors: { request: 'Failed to change password. Please try again later' } });
  }
};

export default function RecoverPassword() {
  const actionData = useActionData();
  const loaderData = useLoaderData();


  return (
    <>
      <div className="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-10">
          <div>
            <img
              className="mx-auto h-16 w-auto"
              src={logo}
              alt="RÖEN"
            />
            <h2 className="mt-4 text-center text-4xl font-bold leading-9 tracking-tight text-gray-900">
              Cambiar contraseña
            </h2>
          </div>

          <Form method="post" className="space-y-6">
            <input hidden id="recovery_code" name="recovery_code" defaultValue={loaderData.recoveryCode} />
            <input hidden id="email" name="email" defaultValue={loaderData.email} />

            <div className="relative -space-y-px rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-0 z-10 rounded-md ring-1 ring-inset ring-gray-300" />
              <div>
                <label htmlFor="password" className="sr-only">
                  Contraseña
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  className={
                    actionData?.errors
                      ? "relative block w-full rounded-t-md border-1 py-1.5 text-gray-900 ring-1 ring-inset ring-rose-500 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "relative block w-full rounded-t-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  }
                  placeholder="Nueva contraseña"
                />
              </div>

              <div>
                <label htmlFor="password_check" className="sr-only">
                  Confirmar contraseña
                </label>
                <input
                  id="password_check"
                  name="password_check"
                  type="password"
                  className={
                    actionData?.errors
                      ? "block w-full rounded-b-md border-1 py-1.5 text-gray-900 ring-1 ring-inset ring-rose-500 placeholder:text-rose-400 focus:ring-2 focus:ring-inset focus:ring-rose-600 sm:text-sm sm:leading-6"
                      : "block w-full rounded-b-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  }
                  placeholder="Confirmar contraseña"
                />
              </div>
            </div>

            {(actionData?.errors && (actionData?.errors?.password || actionData?.errors?.password_check)) && <div>
              <p className="-my-3 text-left text-sm leading-6 text-rose-600">
                Contraseña y/o confirmacion de contraseña inválida
              </p>
            </div>}

            {(actionData?.errors && actionData?.errors?.request) && <div>
              <p className="-my-3 text-left text-sm leading-6 text-rose-600">
                Cambio de contraseña fallido. Intenta de nuevo más tarde
              </p>
            </div>}

            {actionData?.success && <div>
              <p className="-my-3 text-left text-sm leading-6 text-green-600">
                Tu contraseña ha sido cambiada exitosamente
              </p>
            </div>}

            <div>
              <button
                type="submit"
                className="disabled:bg-gray-300 disabled:text-gray-900 disabled:opacity-70 flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                disabled={actionData?.success}
              >
                Enviar
              </button>
            </div>

            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <p className="-py-5 block text-sm leading-6 text-gray-500">
                  ¿Tu contraseña ya ha sido cambiada? <a href="/" className="font-semibold text-indigo-600 hover:text-indigo-500">Inicia sesión</a>
                </p>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </>
  )
};
